<script>
    import Content from "@/services/Content";

    // Share a produced video cut to the connected social platforms. The video is
    // taken from a URL (the editor's rendered cut) rather than an uploaded file,
    // so this is the create-post *video* form minus the attach/upload step.
    export default {
        name: "ModalSharePost",
        props: {
            // The cut being shared. Provides the video URLs (url_crop / url_full)
            // and prefill content (label / description / hashtags).
            cut: { type: Object, default: null },
        },
        data() {
            return {
                showModal: false,
                accountsLoaded: false,
                // Profile / connection state
                username: process.env.VUE_APP_TENANT || "",
                profile: {},
                // Connected social accounts
                socialAccounts: [],
                loadingSocialAccounts: false,
                // Facebook / LinkedIn pages
                facebookPages: [],
                loadingFacebookPages: false,
                linkedinPages: [],
                loadingLinkedinPages: false,
                knownPlatforms: [
                    { key: "instagram", label: "Instagram", icon: "mdi mdi-instagram" },
                    { key: "tiktok", label: "TikTok", icon: "mdi mdi-music-note" },
                    { key: "x", label: "X (Twitter)", icon: "mdi mdi-twitter" },
                    { key: "facebook", label: "Facebook", icon: "mdi mdi-facebook" },
                    { key: "youtube", label: "YouTube", icon: "mdi mdi-youtube" },
                    { key: "linkedin", label: "LinkedIn", icon: "mdi mdi-linkedin" },
                ],
                isPublishing: false,
                scheduleType: "immediately",
                // Which platform the preview pane is mimicking.
                previewPlatform: "instagram",
                // Which rendition to publish when the cut has both.
                videoSource: "crop",
                // Per-platform video requirements (upload-post docs). Drives the
                // requirement note; there is no file to validate here.
                videoPlatformRules: {
                    instagram: { display: "MP4 or MOV · up to 300 MB · 3 s–15 min" },
                    tiktok: { display: "MP4, WebM or MOV · up to 4 GB · ≤ 10 min · min 360×360" },
                    facebook: { display: "MP4 or MOV · 3–90 s (posted as a Reel)" },
                    x: { display: "MP4 · up to 1 GB · 0.5 s–4 h · aspect 1:3 to 3:1" },
                    youtube: { display: "Most video formats · up to 256 GB" },
                },
                form: {
                    platforms: [],
                    facebook_page_id: "",
                    target_linkedin_page_id: "",
                    title: "",
                    description: "",
                    scheduled_date: "",
                },
            };
        },
        computed: {
            availablePlatforms() {
                const byPlatform = {};
                (this.socialAccounts || []).forEach(a => { byPlatform[a.platform] = a; });
                const list = this.knownPlatforms.map(p => ({
                    ...p,
                    connected: !!byPlatform[p.key],
                    account: byPlatform[p.key] || null,
                }));
                Object.keys(byPlatform).forEach(key => {
                    if (!this.knownPlatforms.some(p => p.key === key)) {
                        list.push({
                            key,
                            label: key.charAt(0).toUpperCase() + key.slice(1),
                            icon: "mdi mdi-share-variant",
                            connected: true,
                            account: byPlatform[key],
                        });
                    }
                });
                // platforms hidden for now
                const hidden = ["linkedin", "tiktok"];
                return list.filter(p => !hidden.includes(p.key));
            },
            // Video posts can target every available platform.
            visiblePlatforms() {
                return this.availablePlatforms;
            },
            connectedPlatforms() {
                return this.visiblePlatforms.filter(p => p.connected);
            },
            needsFacebookPage() {
                return this.form.platforms.includes("facebook");
            },
            needsLinkedinPage() {
                return this.form.platforms.includes("linkedin");
            },
            facebookVideoCaption() {
                // On Facebook the video is posted as a Reel — the description is the caption.
                return this.form.platforms.includes("facebook");
            },
            profileName() {
                return this.profile.username || this.username || "Your page";
            },
            avatarInitials() {
                return this.profileName.slice(0, 2).toUpperCase();
            },
            showDescription() {
                const platformsUsingDescription = ["youtube", "facebook", "linkedin"];
                return this.form.platforms.some(p => platformsUsingDescription.includes(p));
            },
            descriptionHint() {
                return "Optional extended text used only on LinkedIn commentary, Facebook descriptions, YouTube descriptions, and Pinterest notes. Ignored elsewhere.";
            },
            // Per-platform requirement lines shown for the selected platforms.
            videoRequirements() {
                const labels = { instagram: "Instagram", tiktok: "TikTok", x: "X (Twitter)", facebook: "Facebook", youtube: "YouTube" };
                return this.form.platforms
                    .filter(p => this.videoPlatformRules[p] && this.videoPlatformRules[p].display)
                    .map(p => ({ platform: p, label: labels[p] || p, display: this.videoPlatformRules[p].display }));
            },
            hasBothRenditions() {
                const cut = this.cut || {};
                return !!(cut.url_crop && cut.url_full);
            },
            // Platforms offered as preview tabs — only the connected ones.
            previewPlatforms() {
                return this.connectedPlatforms;
            },
            // Caption text shown inside every platform mock.
            captionText() {
                return this.form.title || "Your post preview will appear here.";
            },
            // @handle used by the X / TikTok mocks.
            handle() {
                return (this.profileName || "yourpage").toLowerCase().replace(/\s+/g, "");
            },
            // The URL that will actually be published (and previewed).
            videoUrl() {
                const cut = this.cut || {};
                if (this.videoSource === "full") return cut.url_full || cut.url_crop || "";
                return cut.url_crop || cut.url_full || "";
            },
        },
        watch: {
            "form.platforms"(val) {
                if (val.includes("facebook") && !this.facebookPages.length) {
                    this.loadFacebookPages();
                }
                if (val.includes("linkedin") && !this.linkedinPages.length) {
                    this.loadLinkedinPages();
                }
            },
            scheduleType(val) {
                if (val === "immediately") this.form.scheduled_date = "";
            },
            // Keep the preview on a connected platform once accounts resolve.
            connectedPlatforms(list) {
                if (list.length && !list.some(p => p.key === this.previewPlatform)) {
                    this.previewPlatform = list[0].key;
                }
            },
        },
        methods: {
            open() {
                this.resetForm();
                this.prefillFromCut();
                this.videoSource = (this.cut && this.cut.url_crop) ? "crop" : "full";
                this.showModal = true;
                if (!this.accountsLoaded) {
                    this.loadSocialAccounts();
                    this.loadProfile();
                }
            },
            close() {
                this.showModal = false;
            },
            prefillFromCut() {
                const cut = this.cut || {};
                const tags = (cut.hashtags || [])
                    .map(t => String(t).trim())
                    .filter(Boolean)
                    .map(t => (t.startsWith("#") ? t : "#" + t))
                    .join(" ");
                this.form.title = [cut.label, tags].filter(Boolean).join("\n\n");
                this.form.description = cut.description || "";
            },
            togglePlatform(p) {
                if (!p.connected) return;
                const i = this.form.platforms.indexOf(p.key);
                if (i === -1) {
                    this.form.platforms.push(p.key);
                } else {
                    this.form.platforms.splice(i, 1);
                }
            },
            platformIcon(platform) {
                const p = this.knownPlatforms.find(k => k.key === platform);
                return p ? p.icon : "mdi mdi-share-variant";
            },
            parseError(error) {
                const e = error && error.response && error.response.data ? error.response.data.error : null;
                if (Array.isArray(e)) return e.join("<br>");
                return e || "Something went wrong. Please try again.";
            },
            async loadSocialAccounts() {
                this.loadingSocialAccounts = true;
                try {
                    const response = await Content.getSocialAccounts();
                    this.socialAccounts = response.data.data || [];
                    this.accountsLoaded = true;
                } catch (error) {
                    this.socialAccounts = [];
                    this.failedmsg("Connected accounts", this.parseError(error));
                } finally {
                    this.loadingSocialAccounts = false;
                }
            },
            async loadProfile() {
                try {
                    const response = await Content.getUploadPostProfile(this.username);
                    this.profile = response.data.data || {};
                } catch (error) {
                    this.profile = {};
                }
            },
            async loadFacebookPages() {
                this.loadingFacebookPages = true;
                try {
                    const response = await Content.getUploadPostFacebookPages();
                    this.facebookPages = (response.data.data && response.data.data.pages) ? response.data.data.pages : [];
                } catch (error) {
                    this.facebookPages = [];
                    this.failedmsg("Facebook Pages", this.parseError(error));
                } finally {
                    this.loadingFacebookPages = false;
                }
            },
            async loadLinkedinPages() {
                this.loadingLinkedinPages = true;
                try {
                    const response = await Content.getUploadPostLinkedinPages();
                    this.linkedinPages = (response.data.data && response.data.data.pages) ? response.data.data.pages : [];
                } catch (error) {
                    this.linkedinPages = [];
                    this.failedmsg("LinkedIn Pages", this.parseError(error));
                } finally {
                    this.loadingLinkedinPages = false;
                }
            },
            buildFormData() {
                const fd = new FormData();
                // platforms are sent as a JSON-encoded string in platform[0]
                fd.append("platform[0]", JSON.stringify(this.form.platforms));
                if (this.form.title) fd.append("title", this.form.title);
                if (this.showDescription && this.form.description) fd.append("description", this.form.description);
                if (this.needsFacebookPage && this.form.facebook_page_id) {
                    fd.append("facebook_page_id", this.form.facebook_page_id);
                }
                if (this.needsLinkedinPage && this.form.target_linkedin_page_id) {
                    fd.append("target_linkedin_page_id", this.form.target_linkedin_page_id);
                }
                if (this.form.scheduled_date) {
                    fd.append("scheduled_date", new Date(this.form.scheduled_date).toISOString());
                }
                return fd;
            },
            async publish() {
                if (!this.form.platforms.length) {
                    this.failedmsg("Select a platform", "Choose at least one connected platform to publish to.");
                    return;
                }
                if (this.needsFacebookPage && !this.form.facebook_page_id) {
                    this.failedmsg("Facebook Page required", "Select a Facebook Page to publish to Facebook.");
                    return;
                }
                if (!this.videoUrl) {
                    this.failedmsg("Video unavailable", "This cut has no rendered video to share yet.");
                    return;
                }

                this.isPublishing = true;
                try {
                    const fd = this.buildFormData();
                    // The video is shared by URL (the editor's rendered cut) instead of
                    // an uploaded file — upload-post accepts a URL in the `video` field.
                    fd.append("video", this.videoUrl);
                    const response = await Content.uploadPostUploadVideo(fd);

                    if (response && response.data && response.data.data) {
                        this.successmsg("Post submitted", "Your video has been sent to the selected platforms.");
                        this.$emit("published", { cut: this.cut });
                        this.close();
                    } else {
                        this.failedmsg("Publish failed", "Unexpected response from the server.");
                    }
                } catch (error) {
                    this.failedmsg("Publish failed", this.parseError(error));
                } finally {
                    this.isPublishing = false;
                }
            },
            resetForm() {
                this.form.platforms = [];
                this.form.facebook_page_id = "";
                this.form.target_linkedin_page_id = "";
                this.form.title = "";
                this.form.description = "";
                this.form.scheduled_date = "";
                this.scheduleType = "immediately";
            },
        },
    };
</script>

<template>
    <b-modal id="share_post" v-model="showModal" size="xl" title-class="font-18" hide-footer>
        <template v-slot:modal-header>
            <div class="w-100">
                <h5 class="modal-title mb-0">Share to social</h5>
                <p class="text-muted mb-0 mt-2">
                    Publish this cut to your connected platforms
                    <router-link to="/content/social_upload" target="_blank" class="ms-1">
                        <i class="mdi mdi-cog-outline me-1"></i>Manage social networks
                    </router-link>
                </p>
            </div>
            <button type="button" class="btn-close" @click="close" aria-label="Close"></button>
        </template>

        <div class="row">
            <!-- Composer -->
            <div class="col-lg-7">
                <!-- Platforms -->
                <div v-if="loadingSocialAccounts" class="text-muted py-2 mb-2">
                    <b-spinner small class="me-2"></b-spinner>Loading accounts...
                </div>
                <template v-else>
                    <div v-if="!connectedPlatforms.length" class="alert alert-info py-2">
                        No connected platforms yet. Connect accounts on the
                        <router-link to="/content/social_upload">Social Upload</router-link> page.
                    </div>
                    <div class="chip-row mb-3">
                        <button
                            v-for="p in visiblePlatforms"
                            :key="p.key"
                            type="button"
                            class="platform-chip"
                            :class="{ 'platform-chip--active': form.platforms.includes(p.key), 'platform-chip--off': !p.connected }"
                            :disabled="!p.connected"
                            @click="togglePlatform(p)"
                        >
                            <i :class="p.icon" class="me-1"></i>{{ p.label }}
                        </button>
                    </div>
                </template>

                <!-- Facebook page -->
                <div v-if="needsFacebookPage" class="mb-3">
                    <label class="form-label">Facebook Page <span class="text-danger">*</span></label>
                    <div v-if="loadingFacebookPages" class="text-muted">
                        <b-spinner small class="me-2"></b-spinner>Loading pages...
                    </div>
                    <select v-else v-model="form.facebook_page_id" class="form-select">
                        <option value="" disabled>Select a Facebook Page</option>
                        <option v-for="page in facebookPages" :key="page.id" :value="page.id">
                            {{ page.name }} ({{ page.id }})
                        </option>
                    </select>
                    <small v-if="!loadingFacebookPages && !facebookPages.length" class="text-muted">
                        No Facebook Pages found for this profile.
                    </small>
                </div>

                <!-- LinkedIn page (optional) -->
                <div v-if="needsLinkedinPage" class="mb-3">
                    <label class="form-label">LinkedIn Page</label>
                    <div v-if="loadingLinkedinPages" class="text-muted">
                        <b-spinner small class="me-2"></b-spinner>Loading pages...
                    </div>
                    <select v-else v-model="form.target_linkedin_page_id" class="form-select">
                        <option value="">Post to my personal profile</option>
                        <option v-for="page in linkedinPages" :key="page.id" :value="page.id">
                            {{ page.name }} ({{ page.id }})
                        </option>
                    </select>
                </div>

                <!-- Content -->
                <div class="mb-3">
                    <label class="form-label">Content</label>
                    <b-form-textarea
                        v-model="form.title"
                        rows="4"
                        placeholder="Write your post content..."
                    ></b-form-textarea>
                    <div class="text-end text-muted font-size-12 mt-1">
                        {{ (form.title || "").length }} characters
                    </div>
                    <div v-if="facebookVideoCaption" class="alert alert-warning py-2 mt-2 mb-0">
                        <i class="mdi mdi-facebook me-1"></i>
                        On <strong>Facebook</strong>, video posts are published as Reels — the
                        <strong>Description</strong> below becomes the post caption, not this Content.
                    </div>
                </div>

                <!-- Description (only when a platform that uses it is selected) -->
                <div v-if="showDescription" class="mb-3">
                    <label class="form-label">Description</label>
                    <b-form-textarea
                        v-model="form.description"
                        rows="2"
                        placeholder="Optional additional description..."
                    ></b-form-textarea>
                    <small class="text-muted">{{ descriptionHint }}</small>
                </div>

                <!-- Video requirements -->
                <div v-if="videoRequirements.length" class="alert alert-info py-2">
                    <div class="fw-semibold mb-1">
                        <i class="mdi mdi-information-outline me-1"></i>Video requirements for the selected platforms
                    </div>
                    <ul class="mb-0 ps-3">
                        <li v-for="r in videoRequirements" :key="r.platform">
                            <i :class="platformIcon(r.platform)" class="me-1"></i><strong>{{ r.label }}:</strong> {{ r.display }}
                        </li>
                    </ul>
                </div>

                <!-- Rendition toggle (when the cut has both) -->
                <div v-if="hasBothRenditions" class="mb-3">
                    <label class="form-label d-block">Video to share</label>
                    <div class="btn-group btn-group-sm" role="group">
                        <button
                            type="button"
                            class="btn"
                            :class="videoSource === 'crop' ? 'btn-primary' : 'btn-outline-primary'"
                            @click="videoSource = 'crop'"
                        >
                            <i class="mdi mdi-phone-rotate-portrait me-1"></i>Vertical
                        </button>
                        <button
                            type="button"
                            class="btn"
                            :class="videoSource === 'full' ? 'btn-primary' : 'btn-outline-primary'"
                            @click="videoSource = 'full'"
                        >
                            <i class="mdi mdi-phone-rotate-landscape me-1"></i>Horizontal
                        </button>
                    </div>
                </div>

                <!-- Schedule type -->
                <div class="card border mt-2 mb-0">
                    <div class="card-body py-3">
                        <div class="fw-semibold mb-2">
                            <i class="mdi mdi-clock-outline me-1"></i>Select schedule type
                        </div>
                        <b-form-radio v-model="scheduleType" name="share-schedule" value="immediately" class="mb-2">
                            <span class="ms-2">Immediately</span>
                        </b-form-radio>
                        <b-form-radio v-model="scheduleType" name="share-schedule" value="custom">
                            <span class="ms-2">Custom time</span>
                        </b-form-radio>
                        <div v-if="scheduleType === 'custom'" class="mt-2">
                            <b-form-input v-model="form.scheduled_date" type="datetime-local"></b-form-input>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Post preview -->
            <div class="col-lg-5">
                <h5 class="mb-0">Post Preview</h5>
                <p class="text-muted font-size-12 mb-3">Social networks may alter formatting</p>

                <div v-if="loadingSocialAccounts" class="text-muted py-4 text-center">
                    <b-spinner small class="me-2"></b-spinner>Loading platforms...
                </div>
                <div v-else-if="!previewPlatforms.length" class="alert alert-info py-2 mb-0">
                    No connected platforms to preview. Connect accounts on the
                    <router-link to="/content/social_upload">Social Upload</router-link> page.
                </div>
                <template v-else>
                <!-- Platform tabs -->
                <div class="preview-tabs">
                    <button
                        v-for="p in previewPlatforms"
                        :key="p.key"
                        type="button"
                        class="preview-tab"
                        :class="{ 'preview-tab--active': previewPlatform === p.key }"
                        @click="previewPlatform = p.key"
                    >{{ p.label }}</button>
                </div>

                <!-- Instagram -->
                <div v-if="previewPlatform === 'instagram'" class="pv-card">
                    <div class="pv-head">
                        <span class="pv-avatar pv-avatar--ig">{{ avatarInitials }}</span>
                        <span class="pv-user">{{ handle }}</span>
                        <i class="mdi mdi-dots-horizontal ms-auto text-muted"></i>
                    </div>
                    <div class="pv-media pv-media--ig">
                        <video v-if="videoUrl" :key="videoUrl" :src="videoUrl" controls preload="metadata"></video>
                        <span v-else class="pv-media-empty">No media added</span>
                    </div>
                    <div class="pv-ig-actions">
                        <i class="mdi mdi-heart-outline"></i>
                        <i class="mdi mdi-comment-outline"></i>
                        <i class="mdi mdi-send-outline"></i>
                        <i class="mdi mdi-bookmark-outline ms-auto"></i>
                    </div>
                    <div class="pv-ig-likes">1,204 likes</div>
                    <div class="pv-ig-caption"><strong>{{ handle }}</strong> {{ captionText }}</div>
                </div>

                <!-- X (Twitter) -->
                <div v-else-if="previewPlatform === 'x'" class="pv-card pv-card--pad">
                    <div class="d-flex">
                        <span class="pv-avatar pv-avatar--x">{{ avatarInitials }}</span>
                        <div class="ms-2 flex-grow-1 min-w-0">
                            <div class="pv-x-name">
                                <strong>{{ profileName }}</strong>
                                <span class="text-muted"> @{{ handle }} · now</span>
                            </div>
                            <div class="pv-body-text">{{ captionText }}</div>
                            <div class="pv-media pv-media--x">
                                <video v-if="videoUrl" :key="videoUrl" :src="videoUrl" controls preload="metadata"></video>
                                <span v-else class="pv-media-empty">No media added</span>
                            </div>
                            <div class="pv-x-actions">
                                <span><i class="mdi mdi-comment-outline"></i> 12</span>
                                <span><i class="mdi mdi-repeat-variant"></i> 34</span>
                                <span><i class="mdi mdi-heart-outline"></i> 208</span>
                                <span><i class="mdi mdi-chart-bar"></i> 5.6K</span>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Facebook -->
                <div v-else-if="previewPlatform === 'facebook'" class="pv-card">
                    <div class="pv-head">
                        <span class="pv-avatar pv-avatar--fb">{{ avatarInitials }}</span>
                        <div class="min-w-0">
                            <div class="fw-semibold">{{ profileName }}</div>
                            <div class="text-muted pv-sub">Just now · <i class="mdi mdi-earth"></i></div>
                        </div>
                        <i class="mdi mdi-dots-horizontal ms-auto text-muted"></i>
                    </div>
                    <div class="pv-body-text px-2 pb-2">{{ captionText }}</div>
                    <div class="pv-media pv-media--fb">
                        <video v-if="videoUrl" :key="videoUrl" :src="videoUrl" controls preload="metadata"></video>
                        <span v-else class="pv-media-empty">No media added</span>
                    </div>
                    <div class="pv-fb-actions">
                        <span><i class="mdi mdi-thumb-up-outline"></i> Like</span>
                        <span><i class="mdi mdi-comment-outline"></i> Comment</span>
                        <span><i class="mdi mdi-share-outline"></i> Share</span>
                    </div>
                </div>

                <!-- TikTok -->
                <div v-else-if="previewPlatform === 'tiktok'" class="pv-card pv-tt">
                    <div class="pv-media pv-media--tt">
                        <video v-if="videoUrl" :key="videoUrl" :src="videoUrl" controls preload="metadata"></video>
                        <span v-else class="pv-media-empty">No media added</span>
                        <div class="pv-tt-rail">
                            <span class="pv-avatar pv-avatar--tt">{{ avatarInitials }}</span>
                            <span class="pv-tt-btn"><i class="mdi mdi-heart"></i><small>1.2K</small></span>
                            <span class="pv-tt-btn"><i class="mdi mdi-comment"></i><small>84</small></span>
                            <span class="pv-tt-btn"><i class="mdi mdi-share"></i><small>Share</small></span>
                        </div>
                        <div class="pv-tt-caption">
                            <div class="fw-semibold">@{{ handle }}</div>
                            <div class="pv-tt-text">{{ captionText }}</div>
                        </div>
                    </div>
                </div>

                <!-- YouTube -->
                <div v-else-if="previewPlatform === 'youtube'" class="pv-card">
                    <div class="pv-media pv-media--yt">
                        <video v-if="videoUrl" :key="videoUrl" :src="videoUrl" controls preload="metadata"></video>
                        <span v-else class="pv-media-empty">No media added</span>
                    </div>
                    <div class="pv-yt-meta">
                        <span class="pv-avatar pv-avatar--yt">{{ avatarInitials }}</span>
                        <div class="min-w-0">
                            <div class="pv-yt-title">{{ captionText }}</div>
                            <div class="text-muted pv-sub">{{ profileName }} · Just now</div>
                        </div>
                    </div>
                </div>

                <!-- Generic fallback -->
                <div v-else class="pv-card">
                    <div class="pv-head">
                        <span class="pv-avatar">{{ avatarInitials }}</span>
                        <span class="pv-user">{{ profileName }}</span>
                    </div>
                    <div class="pv-media pv-media--fb">
                        <video v-if="videoUrl" :key="videoUrl" :src="videoUrl" controls preload="metadata"></video>
                        <span v-else class="pv-media-empty">No media added</span>
                    </div>
                    <div class="pv-body-text p-2">{{ captionText }}</div>
                </div>

                <p class="text-muted font-size-12 mt-3 mb-0">
                    <i class="mdi mdi-circle-small"></i>
                    <template v-if="scheduleType === 'custom' && form.scheduled_date">
                        This post will be published automatically at your scheduled time
                    </template>
                    <template v-else>
                        This post will be published immediately
                    </template>
                </p>
                </template>
            </div>
        </div>

        <!-- Footer -->
        <div class="d-flex align-items-center justify-content-between mt-3 pt-3 border-top">
            <span class="text-muted">
                <i class="mdi mdi-check-circle-outline me-1"></i>{{ form.platforms.length }} platform(s) selected
            </span>
            <div>
                <b-button variant="light" class="me-2" @click="close">Cancel</b-button>
                <button type="button" class="btn btn-primary" :disabled="isPublishing" @click="publish">
                    <span v-if="!isPublishing"><i class="mdi mdi-send me-1"></i>Publish Now</span>
                    <span v-else>Publishing...</span>
                </button>
            </div>
        </div>
    </b-modal>
</template>

<style scoped>
    /* Platform chips */
    .chip-row {
        display: flex;
        flex-wrap: wrap;
        gap: 0.5rem;
    }
    .platform-chip {
        display: inline-flex;
        align-items: center;
        border: 1px solid #ced4da;
        background: #fff;
        color: #495057;
        border-radius: 999px;
        padding: 0.35rem 0.85rem;
        font-size: 0.8125rem;
        transition: all 0.15s ease;
    }
    .platform-chip:hover:not(.platform-chip--off) {
        border-color: #556ee6;
        color: #556ee6;
    }
    .platform-chip--active {
        border-color: #556ee6;
        background: #eef1ff;
        color: #556ee6;
        font-weight: 600;
    }
    .platform-chip--off {
        opacity: 0.5;
        cursor: not-allowed;
    }

    .min-w-0 { min-width: 0; }

    /* ── Preview: platform tabs ─────────────────────────────────────────────── */
    .preview-tabs {
        display: flex;
        gap: 0.25rem;
        overflow-x: auto;
        border-bottom: 1px solid #eff2f7;
        margin-bottom: 0.85rem;
        padding-bottom: 0.35rem;
    }
    .preview-tab {
        flex: 0 0 auto;
        border: none;
        background: transparent;
        color: #74788d;
        font-size: 0.8125rem;
        font-weight: 500;
        padding: 0.35rem 0.75rem;
        border-radius: 0.5rem;
        white-space: nowrap;
        transition: all 0.15s ease;
    }
    .preview-tab:hover { color: #556ee6; }
    .preview-tab--active {
        background: #eef1ff;
        color: #556ee6;
        font-weight: 600;
    }

    /* ── Preview: shared card ───────────────────────────────────────────────── */
    .pv-card {
        border: 1px solid #eff2f7;
        border-radius: 0.65rem;
        overflow: hidden;
        background: #fff;
        box-shadow: 0 1px 4px rgba(18, 38, 63, 0.06);
    }
    .pv-card--pad { padding: 0.85rem; }

    .pv-head {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0.6rem 0.75rem;
    }
    .pv-avatar {
        width: 34px;
        height: 34px;
        border-radius: 50%;
        flex: 0 0 34px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: #556ee6;
        color: #fff;
        font-weight: 600;
        font-size: 12px;
    }
    .pv-avatar--ig { background: radial-gradient(circle at 30% 107%, #fdf497 0%, #fd5949 45%, #d6249f 60%, #285AEB 90%); }
    .pv-avatar--x  { background: #0f1419; }
    .pv-avatar--fb { background: #1877F2; }
    .pv-avatar--tt { background: #111; border: 1px solid #fff; }
    .pv-avatar--yt { background: #FF0000; }
    .pv-user { font-weight: 600; font-size: 0.875rem; }
    .pv-sub { font-size: 0.75rem; }

    .pv-media {
        position: relative;
        width: 100%;
        background: #f0f0f3;
        display: flex;
        align-items: center;
        justify-content: center;
        overflow: hidden;
    }
    .pv-media video {
        width: 100%;
        height: 100%;
        object-fit: contain;
        display: block;
        background: #000;
    }
    .pv-media-empty {
        color: #adb5bd;
        font-size: 0.9rem;
        padding: 3rem 0;
    }
    .pv-media--ig { aspect-ratio: 4 / 5; }
    .pv-media--x  { aspect-ratio: 16 / 9; border-radius: 0.75rem; margin-top: 0.5rem; }
    .pv-media--fb { aspect-ratio: 1 / 1; }
    .pv-media--yt { aspect-ratio: 16 / 9; background: #000; }
    .pv-media--tt { aspect-ratio: 9 / 16; max-height: 480px; background: #000; }

    .pv-body-text {
        white-space: pre-wrap;
        color: #1c1e21;
        font-size: 0.875rem;
    }

    /* Instagram */
    .pv-ig-actions {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        font-size: 1.35rem;
        color: #1c1e21;
        padding: 0.5rem 0.75rem 0.25rem;
    }
    .pv-ig-likes { font-weight: 600; font-size: 0.85rem; padding: 0 0.75rem; }
    .pv-ig-caption {
        font-size: 0.85rem;
        color: #1c1e21;
        padding: 0.25rem 0.75rem 0.85rem;
        white-space: pre-wrap;
    }

    /* X (Twitter) */
    .pv-x-name { font-size: 0.875rem; }
    .pv-x-actions {
        display: flex;
        justify-content: space-between;
        max-width: 260px;
        margin-top: 0.6rem;
        color: #536471;
        font-size: 0.8rem;
    }

    /* Facebook */
    .pv-fb-actions {
        display: flex;
        justify-content: space-around;
        border-top: 1px solid #eff2f7;
        padding: 0.5rem 0;
        color: #65676b;
        font-size: 0.85rem;
        font-weight: 500;
    }

    /* TikTok */
    .pv-tt .pv-media--tt { border-radius: 0.65rem; }
    .pv-tt-rail {
        position: absolute;
        right: 8px;
        bottom: 70px;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 1rem;
        color: #fff;
    }
    .pv-tt-btn {
        display: flex;
        flex-direction: column;
        align-items: center;
        font-size: 1.5rem;
        line-height: 1;
    }
    .pv-tt-btn small { font-size: 0.7rem; margin-top: 2px; }
    .pv-tt-caption {
        position: absolute;
        left: 10px;
        right: 60px;
        bottom: 12px;
        color: #fff;
        font-size: 0.8rem;
        text-shadow: 0 1px 2px rgba(0, 0, 0, 0.6);
    }
    .pv-tt-text { white-space: pre-wrap; }

    /* YouTube */
    .pv-yt-meta {
        display: flex;
        align-items: flex-start;
        gap: 0.5rem;
        padding: 0.6rem 0.75rem;
    }
    .pv-yt-title {
        font-weight: 600;
        font-size: 0.875rem;
        color: #0f0f0f;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
    }
</style>
